import { registerLocaleData } from '@angular/common';
import locale from '@angular/common/locales/it';
import { enableProdMode, LOCALE_ID, importProvidersFrom, inject, provideAppInitializer } from '@angular/core';

import dayjs from 'dayjs';
import { DEBUG_INFO_ENABLED, SERVER_API_URL } from './app/app.constants';
import { AppConfiguration, IAppConfiguration } from './app/shared/model/app-configuration.model';
import { Title, bootstrapApplication } from '@angular/platform-browser';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { fontAwesomeIcons } from './app/config/font-awesome-icons';
import { NgbDateAdapter, NgbCollapseModule, NgbDatepickerConfig, NgbDropdownModule, NgbModalConfig } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectConfig } from '@ng-select/ng-select';
import { NgbDateDayjsAdapter } from './app/config/datepicker-adapter';
import { ApplicationConfigService } from './app/core/config/application-config.service';
import { SessionService } from './app/core/state/session.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { MgpSpinnerService } from './app/shared/service/mgp-spinner.service';
import { provideNgxWebstorage, withNgxWebstorageConfig, withSessionStorage } from 'ngx-webstorage';
import { httpInterceptorProviders } from './app/core/interceptors';
import { provideRouter } from '@angular/router';
import { provideAnimations } from '@angular/platform-browser/animations';
import { routes } from './app/app.routes';
import { ToastrModule } from 'ngx-toastr';
import { MainComponent } from './app/layout/main/main.component';

if (!DEBUG_INFO_ENABLED) {
  enableProdMode();
}

normalizeLocalMgpPath();

function fetchConfig(): Promise<AppConfiguration> {
  return window.fetch('/assets/configuration/config.json')
    .then(response => response.json())
    .then(response => new AppConfiguration(response as IAppConfiguration));
}

fetchConfig().then((config: AppConfiguration) => {
  (window as any)['MGP_APP_CONFIG'] = config;

  bootstrapApplication(MainComponent, {
    providers: [
        importProvidersFrom(FontAwesomeModule, NgbCollapseModule, NgbDropdownModule,
        ToastrModule.forRoot({
            maxOpened: 1,
            autoDismiss: true,
            tapToDismiss: false,
            closeButton: true,
            positionClass: 'toast-top-center'
        })),
        provideRouter(routes),
        Title,
        { provide: LOCALE_ID, useValue: 'it' },
        { provide: NgbDateAdapter, useClass: NgbDateDayjsAdapter },
        {
            provide: AppConfiguration,
            useFactory() {
                const appConfig = (window as any)['MGP_APP_CONFIG'];
                if (!appConfig) {
                    console.error('Configurazione non trovata in window.MGP_APP_CONFIG');
                    // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
                    return new AppConfiguration({ oscatUrl: '' } as any); // Fallback per non rompere i componenti
                }
                // eslint-disable-next-line @typescript-eslint/no-unsafe-return
                return appConfig;
            }
        },
        {
            provide: NgxSpinnerService,
            useClass: MgpSpinnerService
        },
        provideNgxWebstorage(withNgxWebstorageConfig({
            prefix: 'mgp',
            separator: '-',
            caseSensitive: true
        }), withSessionStorage()),
        httpInterceptorProviders,
        provideHttpClient(withInterceptorsFromDi()),
        provideAnimations(),
        provideAppInitializer(() => {
            const applicationConfigService = inject(ApplicationConfigService);
            const iconLibrary = inject(FaIconLibrary);
            const dpConfig = inject(NgbDatepickerConfig);
            const ngbModalService = inject(NgbModalConfig);
            const selectConfig = inject(NgSelectConfig);

            applicationConfigService.setEndpointPrefix(SERVER_API_URL);
            ngbModalService.backdrop = 'static';
            ngbModalService.keyboard = false;

            selectConfig.loadingText = 'Sto cercando...';

            registerLocaleData(locale);
            iconLibrary.addIcons(...fontAwesomeIcons);
            dpConfig.minDate = { year: dayjs().subtract(100, 'year').year(), month: 1, day: 1 };

            return inject(SessionService).login();
        })
    ]
})
    .catch(err => console.error(err));
});

function normalizeLocalMgpPath(): void {
  const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  const { pathname, search, hash } = window.location;

  if (!isLocalhost || !pathname.startsWith('/mgp')) {
    return;
  }

  const normalizedPath = pathname === '/mgp' ? '/' : pathname.replace(/^\/mgp/, '') || '/';
  window.history.replaceState({}, '', `${normalizedPath}${search}${hash}`);
}
